import type { Metadata, Viewport } from "next";
import { Outfit, Playfair_Display } from "next/font/google";
import ThemeProvider from "@/components/ThemeProvider";
import { OrganizationJsonLd, LocalBusinessJsonLd, WebSiteJsonLd } from "@/components/JsonLd";
import "./globals.css";

const outfit = Outfit({
  variable: "--font-outfit",
  subsets: ["latin"],
  weight: ["400", "500", "700"],
  display: "swap",
});

const playfair = Playfair_Display({
  variable: "--font-playfair",
  subsets: ["latin"],
  weight: ["400", "700"],
  style: ["italic"],
  display: "swap",
});

export const viewport: Viewport = {
  width: "device-width",
  initialScale: 1,
  viewportFit: "cover",
};

export const metadata: Metadata = {
  metadataBase: new URL("https://devcubix.com"),
  title: {
    default: "Dev Cubix — Full-Stack Digital Agency | Islamabad, Pakistan",
    template: "%s | Dev Cubix",
  },
  description:
    "We build impactful brands that shine. Impeccable design and seamless functionality — UI/UX, Web Development, Presentation Design, Graphic Design, AI & Chatbots. 190+ projects delivered from Islamabad, Pakistan.",
  keywords: [
    "digital agency",
    "web development",
    "UI/UX design",
    "graphic design",
    "presentation design",
    "AI chatbots",
    "Islamabad",
    "Pakistan",
    "Dev Cubix",
    "Webflow",
    "Framer",
    "branding",
  ],
  authors: [{ name: "Dev Cubix", url: "https://devcubix.com" }],
  creator: "Dev Cubix",
  publisher: "Dev Cubix",
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  icons: {
    icon: "/icon.svg",
  },
  openGraph: {
    title: "Dev Cubix — Full-Stack Digital Agency",
    description:
      "We build impactful brands that shine. UI/UX, Web Development, Presentation Design, Graphic Design, AI & Chatbots. 190+ projects delivered.",
    url: "https://devcubix.com",
    siteName: "Dev Cubix",
    type: "website",
    locale: "en_US",
    images: [
      {
        url: "/og-image.png",
        width: 1200,
        height: 630,
        alt: "Dev Cubix — Full-Stack Digital Agency",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Dev Cubix — Full-Stack Digital Agency",
    description:
      "UI/UX, Web Development, Presentation Design, Graphic Design, AI & Chatbots. 190+ projects delivered from Islamabad.",
    images: ["/og-image.png"],
    creator: "@devcubix",
  },
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  alternates: {
    canonical: "https://devcubix.com",
  },
};

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Preconnect to external resources for faster loading */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://images.unsplash.com" />
        {/* Web manifest for PWA + SEO */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#E11D24" />
        <OrganizationJsonLd />
        <LocalBusinessJsonLd />
        <WebSiteJsonLd />
      </head>
      <body
        className={`${outfit.variable} ${playfair.variable} font-sans antialiased bg-surface text-foreground overflow-x-hidden`}
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-9999 focus:px-4 focus:py-2 focus:bg-[#E11D24] focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
        >
          Skip to main content
        </a>
        <ThemeProvider>
          <main id="main-content">{children}</main>
        </ThemeProvider>
      </body>
    </html>
  );
}
